
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isDatabaseRecord, toDatabaseValue } from "@/utils/supabase/type-guards";
import { safeSelect } from "@/utils/supabase/queries";

export type SearchResult = {
  id: string;
  type: "user" | "group" | "prompt";
  title: string;
  description?: string;
  url: string;
  category?: string;
  avatarId?: number;
};

type SearchResults = {
  users: SearchResult[];
  groups: SearchResult[];
  prompts: SearchResult[];
};

export function useSearch(query: string, open: boolean) {
  const [results, setResults] = useState<SearchResults>({
    users: [],
    groups: [],
    prompts: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setResults({ users: [], groups: [], prompts: [] });
      return;
    }

    const delayDebounceFn = setTimeout(() => {
      if (query.trim().length >= 2) {
        performSearch(query);
      } else {
        setResults({ users: [], groups: [], prompts: [] });
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query, open]);

  const performSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) return;
    
    setIsLoading(true);
    
    try {
      // Use safeSelect for all queries
      // Use public_profiles view for security - only exposes non-sensitive fields
      const [usersData, groupsData, promptsData] = await Promise.all([
        safeSelect<any>(
          supabase
            .from("public_profiles")
            .select("id, username, bio, avatar_id")
            .ilike("username", `%${searchQuery}%`)
            .limit(5),
          "Failed to search users"
        ),
        safeSelect<any>(
          supabase
            .from("groups")
            .select("id, name, description")
            .ilike("name", `%${searchQuery}%`)
            .limit(5),
          "Failed to search groups"
        ),
        safeSelect<any>(
          supabase
            .from("prompts")
            .select("id, content, title, user_id, geo_metadata")
            .or(`content.ilike.%${searchQuery}%,title.ilike.%${searchQuery}%,geo_metadata->>intent.ilike.%${searchQuery}%,geo_metadata->>use_case.ilike.%${searchQuery}%`)
            .limit(5),
          "Failed to search prompts"
        )
      ]);

      // Safe filtering and mapping with type checks
      const safeUsersData = Array.isArray(usersData) ? usersData.filter(isDatabaseRecord) : [];
      const safeGroupsData = Array.isArray(groupsData) ? groupsData.filter(isDatabaseRecord) : [];
      const safePromptsData = Array.isArray(promptsData) ? promptsData.filter(isDatabaseRecord) : [];

      setResults({
        users: safeUsersData.map(user => ({
          id: user.id,
          type: "user" as const,
          title: user.username || "Unknown User",
          description: user.bio || "User",
          url: `/profile/${user.id}`,
          avatarId: user.avatar_id || 1,
        })),
        
        groups: safeGroupsData.map(group => ({
          id: group.id,
          type: "group" as const,
          title: group.name || "Unknown Group",
          description: group.description || "Community",
          url: `/groups/${group.id}`
        })),
        
        prompts: safePromptsData.map(prompt => {
          const geoMeta = prompt.geo_metadata as any;
          const geoLabel = geoMeta?.intent ? ` • ${geoMeta.intent}` : '';
          return {
            id: prompt.id,
            type: "prompt" as const,
            title: prompt.title || prompt.content?.substring(0, 30) || "Untitled Prompt",
            description: (prompt.content?.substring(0, 100) || "Prompt") + geoLabel,
            url: `/prompt/${prompt.id}`,
            category: geoMeta?.use_case || "prompt-engineering"
          };
        }),
      });
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Search failed. Please try again.");
      setResults({ users: [], groups: [], prompts: [] });
    } finally {
      setIsLoading(false);
    }
  };

  return { results, isLoading };
}
